import React from 'react';
import classNames from 'classnames';

import type { DeviceInfo } from 'js/src/components/app/recorder/types';
import { CAMERA_OFF_DEVICE_ID, DeviceTypes } from 'js/src/components/app/recorder/types';
import Select from 'js/src/components/common/ui/Select/Select';

const DEFAULT_CAMERA_LABEL = 'Select Camera';
const DEFAULT_MICROPHONE_LABEL = 'Select Microphone';
const CAMERA_OFF_LABEL = 'Camera off';

const cleanDeviceLabel = (label: string) => {
    return label.split('(')[0].trim();
};

type Props = {
    isCameraConnected: boolean;
    isMicrophoneConnected: boolean;
    availableCameraDevices: DeviceInfo[];
    availableMicrophoneDevices: DeviceInfo[];
    activeCameraDeviceId?: string;
    activeMicrophoneDeviceId?: string;
    onDeviceChange?: (deviceType: DeviceTypes, deviceId: string) => void;
    /** Hide the camera selector — used by audio-only (voiceover) recording. */
    showCamera?: boolean;
    allowCameraOff?: boolean;
    microphoneToggle?: React.ReactNode;
    className?: string;
};

const DeviceSelectorControls: React.FC<Props> = ({
    isCameraConnected,
    isMicrophoneConnected,
    availableCameraDevices,
    availableMicrophoneDevices,
    activeCameraDeviceId,
    activeMicrophoneDeviceId,
    onDeviceChange,
    showCamera = true,
    allowCameraOff = false,
    microphoneToggle,
    className,
}) => {
    const handleCameraChange = React.useCallback(
        (value: string) => {
            if (onDeviceChange) {
                onDeviceChange(DeviceTypes.VIDEO, value);
            }
        },
        [onDeviceChange],
    );

    const handleMicrophoneChange = React.useCallback(
        (value: string) => {
            if (onDeviceChange) {
                onDeviceChange(DeviceTypes.AUDIO, value);
            }
        },
        [onDeviceChange],
    );

    const availableCameraOptions = React.useMemo(() => {
        const items = availableCameraDevices.map((device) => ({
            label: cleanDeviceLabel(device.label),
            value: device.id,
        }));
        if (allowCameraOff) {
            return [{ label: CAMERA_OFF_LABEL, value: CAMERA_OFF_DEVICE_ID }, ...items];
        }
        return items;
    }, [availableCameraDevices, allowCameraOff]);

    const availableMicrophoneOptions = React.useMemo(() => {
        const items = availableMicrophoneDevices.map((device) => ({
            label: cleanDeviceLabel(device.label),
            value: device.id,
        }));
        return items;
    }, [availableMicrophoneDevices]);

    const activeCameraOption = React.useMemo(() => {
        // When the camera is off, reflect that in the selector rather than
        // leaving the last-used device showing as selected.
        if (allowCameraOff && !isCameraConnected) {
            return availableCameraOptions.find((option) => option.value === CAMERA_OFF_DEVICE_ID);
        }
        return availableCameraOptions.find((option) => option.value === activeCameraDeviceId);
    }, [allowCameraOff, isCameraConnected, activeCameraDeviceId, availableCameraOptions]);

    const activeMicrophoneOption = React.useMemo(() => {
        const activeOption = availableMicrophoneOptions.find(
            (option) => option.value === activeMicrophoneDeviceId,
        );
        return activeOption;
    }, [activeMicrophoneDeviceId, availableMicrophoneOptions]);

    const cameraSelect = (
        <Select<string>
            isMulti={false}
            iconLeft="videocam"
            value={activeCameraOption}
            options={availableCameraOptions}
            placeholder={DEFAULT_CAMERA_LABEL}
            onChange={handleCameraChange}
            isDisabled={!allowCameraOff && !isCameraConnected}
            menuPlacement={'top'}
        />
    );

    const microphoneSelect = (
        <Select<string>
            isMulti={false}
            iconLeft="mic"
            value={activeMicrophoneOption}
            options={availableMicrophoneOptions}
            placeholder={DEFAULT_MICROPHONE_LABEL}
            onChange={handleMicrophoneChange}
            isDisabled={!isMicrophoneConnected}
            menuPlacement={'top'}
        />
    );

    const useStackedLayout = allowCameraOff || !!microphoneToggle;

    if (useStackedLayout) {
        return (
            <div className={classNames('container-fluid gx-0', className)}>
                {showCamera && (
                    <div className="row gx-3 mb-l5-8">
                        <div className="col">{cameraSelect}</div>
                    </div>
                )}
                <div className="row gx-3 align-items-center">
                    <div className="col">{microphoneSelect}</div>
                    {microphoneToggle && <div className="col-auto">{microphoneToggle}</div>}
                </div>
            </div>
        );
    }

    return (
        <div className={classNames('container-fluid gx-0', className)}>
            <div className="row gx-3">
                {showCamera && <div className="col">{cameraSelect}</div>}
                <div className="col">{microphoneSelect}</div>
            </div>
        </div>
    );
};

export default React.memo(DeviceSelectorControls);
